import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import API from '../utils/api';
import { SiDocker, SiKubernetes, SiTerraform, SiJenkins, SiGithubactions, SiAnsible, SiPrometheus, SiGrafana, SiArgo, SiGooglecloud, SiPuppet, SiDigitalocean, SiGnubash, SiElasticstack, SiGitlab, SiDatadog, SiSentry, SiVagrant, SiLinux, SiNginx, SiRabbitmq } from 'react-icons/si';
import { FaAws, FaHeartbeat } from 'react-icons/fa';
import { VscAzure } from 'react-icons/vsc';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const stagger = { show: { transition: { staggerChildren: 0.12 } } };

const TOOLS = [
  { icon: <SiDocker color="#2496ED" />, name: 'Docker' },
  { icon: <SiKubernetes color="#326CE5" />, name: 'Kubernetes' },
  { icon: <FaAws color="#FF9900" />, name: 'AWS' },
  { icon: <VscAzure color="#0089D6" />, name: 'Azure' },
  { icon: <SiGooglecloud color="#4285F4" />, name: 'GCP' },
  { icon: <SiTerraform color="#844FBA" />, name: 'Terraform' },
  { icon: <SiJenkins color="#D24939" />, name: 'Jenkins' },
  { icon: <SiGithubactions color="#2088FF" />, name: 'GitHub Actions' },
  { icon: <SiArgo color="#EF7B4D" />, name: 'Argo CD' },
  { icon: <SiAnsible color="#EE0000" />, name: 'Ansible' },
  { icon: <SiPuppet color="#FFAE1A" />, name: 'Puppet' },
  { icon: <SiGnubash color="#4EAA25" />, name: 'Shell Script' },
  { icon: <SiDigitalocean color="#0080FF" />, name: 'DigitalOcean' },
  { icon: <SiPrometheus color="#E6522C" />, name: 'Prometheus' },
  { icon: <SiGrafana color="#F46800" />, name: 'Grafana' },
  { icon: <SiElasticstack color="#005571" />, name: 'ELK Stack' },
  { icon: <FaHeartbeat color="#0D4887" />, name: 'Nagios' },
  { icon: <SiGitlab color="#FCA121" />, name: 'GitLab CI' },
  { icon: <SiDatadog color="#632CA6" />, name: 'Datadog' },
  { icon: <SiSentry color="#362D59" />, name: 'Sentry' },
  { icon: <SiVagrant color="#1563FF" />, name: 'Vagrant' },
  { icon: <SiLinux color="#FCC624" />, name: 'Linux' },
  { icon: <SiNginx color="#009639" />, name: 'Nginx' },
  { icon: <SiRabbitmq color="#FF6600" />, name: 'RabbitMQ' },
];

const CERTIFICATIONS = [
  { icon: <FaAws color="#FF9900" />, title: 'AWS Solutions Architect' },
  { icon: <VscAzure color="#0089D6" />, title: 'Azure Administrator' },
  { icon: <SiGooglecloud color="#4285F4" />, title: 'GCP Cloud Architect' },
  { icon: <SiKubernetes color="#326CE5" />, title: 'CKA (Kubernetes)' },
  { icon: <SiTerraform color="#844FBA" />, title: 'Terraform Associate' },
  { icon: <SiLinux color="#FCC624" />, title: 'Linux Foundation CKS' },
  { icon: <SiDocker color="#2496ED" />, title: 'Docker Certified' },
  { icon: <FaAws color="#FF9900" />, title: 'AWS SysOps Admin' },
];

const HOW_IT_WORKS = [
  { step: '01', icon: '🎯', title: 'Choose Your Plan', desc: 'Select a plan that fits your learning goals and budget. Get instant access after payment.' },
  { step: '02', icon: '📝', title: 'Submit Tasks', desc: 'Create detailed support requests with files, screenshots, and context. Our experts review them.' },
  { step: '03', icon: '⚡', title: 'Get Expert Help', desc: 'Receive detailed, actionable responses from certified DevOps engineers within your SLA.' },
  { step: '04', icon: '🚀', title: 'Level Up Fast', desc: 'Apply the knowledge, track your progress, and grow your DevOps career exponentially.' },
];

const TESTIMONIALS = [
  {
    name: 'Rahul Sharma',
    role: 'DevOps Engineer @ TCS',
    text: 'This platform transformed my career. Within 3 months of joining, I cleared my AWS certification and got a 40% salary hike. The expert support is phenomenal!',
    rating: 5,
    initials: 'RS',
  },
  {
    name: 'Priya Patel',
    role: 'SRE @ Infosys',
    text: 'The K8s and Terraform modules are gold. I was stuck on production issues for weeks, the team here solved it in 24 hours with detailed explanations.',
    rating: 5,
    initials: 'PP',
  },
  {
    name: 'Arjun Menon',
    role: 'Cloud Architect',
    text: 'Worth every rupee. The personalized support and roadmaps are exactly what I needed. No more generic YouTube tutorials - this is real, applied mentorship.',
    rating: 5,
    initials: 'AM',
  },
];

const TerminalAnimation = () => {
  const [lines, setLines] = useState([]);
  
  useEffect(() => {
    const commands = [
      { text: "docker build -t saas-app .", type: "cmd", delay: 600 },
      { text: "[+] Building 1.2s (8/8) FINISHED", type: "out", delay: 800 },
      { text: "=> exporting to image", type: "out", delay: 300 },
      { text: "kubectl apply -f manifest.yaml", type: "cmd", delay: 1000 },
      { text: "deployment.apps/saas-app created", type: "out", delay: 600 },
      { text: "service/saas-app-svc created", type: "out", delay: 300 },
      { text: "terraform apply -auto-approve", type: "cmd", delay: 1200 },
      { text: "Plan: 5 to add, 0 to change, 0 to destroy.", type: "out", delay: 1500 },
      { text: "Apply complete! Resources: 5 added.", type: "success", delay: 800 },
      { text: "🚀 Infrastructure successfully provisioned.", type: "success", delay: 500 },
    ];

    let currentLine = 0;
    
    const showNextLine = () => {
      if (currentLine >= commands.length) return;
      const cmd = commands[currentLine];
      
      setLines(prev => [...prev, cmd]);
      currentLine++;
      
      if (currentLine < commands.length) {
        setTimeout(showNextLine, commands[currentLine].delay || 400);
      }
    };
    
    const initTimer = setTimeout(showNextLine, 800);
    return () => clearTimeout(initTimer);
  }, []);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      style={{
        background: 'rgba(13, 17, 23, 0.85)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 12,
        overflow: 'hidden',
        overflowX: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.2)',
        textAlign: 'left',
        fontFamily: 'var(--font-mono)',
        fontSize: 13,
        minHeight: 260,
        maxHeight: 380,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        margin: '0 auto',
        maxWidth: 550
      }}
    >
      <div style={{ background: 'rgba(22, 27, 34, 0.95)', padding: '12px 16px', display: 'flex', gap: 8, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ff5f56' }} />
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ffbd2e' }} />
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#27c93f' }} />
        <span style={{ margin: 'auto', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>bash — setup-infra.sh</span>
      </div>
      <div style={{ padding: '24px 20px', whiteSpace: 'pre-wrap', flex: 1, overflowY: 'auto' }}>
        {lines.map((l, i) => (
          <motion.div 
            key={i} 
            initial={{ opacity: 0, x: -5 }} 
            animate={{ opacity: 1, x: 0 }}
            style={{ marginBottom: 6, lineHeight: 1.6 }}
          >
            {l.type === 'cmd' ? (
              <span style={{ color: '#a5d6ff' }}>
                <span style={{ color: '#7ee787', marginRight: 8, fontWeight: 'bold' }}>devops@admin:~$</span>
                {l.text}
              </span>
            ) : l.type === 'success' ? (
              <span style={{ color: '#3fb950', fontWeight: 'bold' }}>{l.text}</span>
            ) : (
              <span style={{ color: '#8b949e' }}>{l.text}</span>
            )}
          </motion.div>
        ))}
        <motion.div
          animate={{ opacity: [1, 0] }}
          transition={{ repeat: Infinity, duration: 0.8 }}
          style={{ display: 'inline-block', width: 8, height: 16, background: '#7ee787', verticalAlign: 'middle', marginTop: lines.length ? 6 : 0 }}
        />
      </div>
    </motion.div>
  );
};

export default function Home() {
  const [plans, setPlans] = useState([]);
  const [developers, setDevelopers] = useState([]);

  useEffect(() => {
    API.get('/plans').then(res => setPlans(res.data.plans)).catch(() => {});
    API.get('/developers').then(res => setDevelopers(res.data.developers)).catch(() => {});
  }, []);

  return (
    <>
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="hero" style={{ minHeight: '85vh', paddingTop: '140px' }}>
        <div className="hero-bg">
          <div className="hero-orb hero-orb-1" />
          <div className="hero-orb hero-orb-2" />
          <div className="hero-orb hero-orb-3" />
        </div>

        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', gap: '48px', alignItems: 'center' }}>
            <motion.div
              className="hero-content"
              variants={stagger}
              initial="hidden"
              animate="show"
              style={{ textAlign: 'left', margin: 0, paddingRight: '20px' }}
            >
              <motion.div variants={fadeUp}>
                <span className="badge">✨ Premium DevOps Mentorship Platform</span>
              </motion.div>

              <motion.h1 className="hero-title" variants={fadeUp} style={{ lineHeight: 1.15, marginTop: 16 }}>
                Master DevOps with{' '}
                <span className="text-gradient">Real-World Support</span>
              </motion.h1>

              <motion.p className="hero-subtitle" variants={fadeUp} style={{ marginLeft: 0 }}>
                Submit your DevOps challenges and get expert solutions from certified engineers.
                Docker, Kubernetes, AWS, Terraform — we've got you covered every step of the way.
              </motion.p>

              <motion.div className="hero-actions" variants={fadeUp} style={{ justifyContent: 'flex-start' }}>
                <Link to="/pricing" className="btn btn-primary btn-lg">
                  🚀 Start Learning Now
                </Link>
                <a href="#how-it-works" className="btn btn-ghost btn-lg">
                  ▶ See How It Works
                </a>
              </motion.div>

              <motion.div className="hero-stats" variants={fadeUp} style={{ justifyContent: 'flex-start', flexWrap: 'wrap', gap: 24, padding: '24px 0 0', border: 'none', background: 'transparent' }}>
                {[
                  { number: '2,500+', label: 'Engineers Trained' },
                  { number: '< 24h', label: 'Response Time' },
                  { number: '50+', label: 'DevOps Topics' },
                ].map((s, i) => (
                  <div key={i} className="stat-item">
                    <div className="stat-number" style={{ fontSize: 24, textAlign: 'left' }}>{s.number}</div>
                    <div className="stat-label" style={{ textAlign: 'left' }}>{s.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Terminal Animation Column */}
            <div style={{ position: 'relative', zIndex: 3, padding: '0 12px' }}>
              <div style={{ position: 'absolute', inset: -20, background: 'radial-gradient(circle at center, rgba(99,102,241,0.15) 0%, transparent 60%)', filter: 'blur(20px)', zIndex: -1 }} />
              <TerminalAnimation />
            </div>
          </div>
        </div>
      </section>

      {/* ===== FREE ARCHITECTURE AUDIT BANNER ===== */}
      <section className="section-sm" style={{ padding: '20px 0 40px' }}>
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            style={{
              background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)',
              borderLeft: '4px solid #10b981',
              borderRight: '1px solid rgba(16, 185, 129, 0.2)',
              borderTop: '1px solid rgba(16, 185, 129, 0.2)',
              borderBottom: '1px solid rgba(16, 185, 129, 0.2)',
              borderRadius: '0 16px 16px 0',
              padding: '32px 40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 24,
            }}
          >
            <div style={{ flex: '1 1 500px' }}>
               <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                 <span style={{ background: '#10b981', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 800, letterSpacing: '0.05em' }}>LIMITED TIME OFFER</span>
                 <h2 style={{ margin: 0, fontSize: 28, color: '#fff', fontWeight: 800 }}>Free Architecture Audit</h2>
               </div>
               <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1.6 }}>
                 Get a comprehensive, completely <strong>free-of-charge</strong> review of your cloud infrastructure. Our certified experts will analyze your architecture to identify security risks, scalability bottlenecks, and uncover ways to significantly reduce your AWS/GCP bills. No commitment required.
               </p>
            </div>
            <Link to="/contact?subject=Free%20Architecture%20Audit" className="btn" style={{ background: '#10b981', color: '#fff', border: 'none', padding: '14px 28px', fontSize: 16, fontWeight: 700, boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.39)', whiteSpace: 'nowrap' }}>
              Claim Your Free Audit →
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===== TOOLS ===== */}
      <section className="section" id="tools">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge">🛠️ Tools Coverage</span>
            <h2 className="section-title">Tools You Will <span className="text-gradient">Master</span></h2>
            <p className="section-subtitle">
              Get expert support on the most in-demand DevOps tools used by top tech companies worldwide.
            </p>
          </motion.div>

          <motion.div
            className="tools-grid"
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {TOOLS.map((tool, i) => (
              <motion.div
                key={i}
                className="tool-card"
                variants={fadeUp}
                whileHover={{ scale: 1.05, y: -8 }}
                style={{ padding: '30px 16px' }}
              >
                <div style={{ fontSize: 46, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {tool.icon}
                </div>
                <span className="tool-name">{tool.name}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== CERTIFICATIONS SLIDER ===== */}
      <section className="section" style={{ padding: '0px 0 80px', overflow: 'hidden' }}>
        <div className="container text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="section-title" style={{ fontSize: '2rem', marginBottom: 40 }}>
              Guided by <span className="text-gradient">Certified Experts</span>
            </h2>
          </motion.div>
        </div>
        
        <div className="marquee-container">
          <div className="marquee-content">
            {/* Render array multiple times to create a guaranteed seamless infinite loop effect across all large screens */}
            {[...CERTIFICATIONS, ...CERTIFICATIONS, ...CERTIFICATIONS].map((cert, i) => (
              <div key={i} className="cert-card">
                <div className="cert-icon">{cert.icon}</div>
                <div className="cert-title">{cert.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="section" id="how-it-works" style={{ background: 'rgba(15,23,42,0.5)' }}>
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge">🔄 Process</span>
            <h2 className="section-title">How It <span className="text-gradient">Works</span></h2>
            <p className="section-subtitle">
              From question to solution in 4 simple steps. No fluff, just expert DevOps mentorship.
            </p>
          </motion.div>

          <motion.div
            className="steps-grid"
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {HOW_IT_WORKS.map((step, i) => (
              <motion.div key={i} className="step-card" variants={fadeUp} whileHover={{ scale: 1.03 }}>
                <div className="step-number">{step.icon}</div>
                <div style={{ display: 'inline-block', background: 'rgba(99,102,241,0.15)', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, color: 'var(--brand-primary)', marginBottom: 12 }}>
                  STEP {step.step}
                </div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== PRICING PREVIEW ===== */}
      <section className="section">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge">💳 Pricing</span>
            <h2 className="section-title">Simple, <span className="text-gradient">Transparent</span> Pricing</h2>
            <p className="section-subtitle">Choose the plan that accelerates your DevOps career. Upgrade or downgrade anytime.</p>
          </motion.div>

          {plans.length > 0 ? (
            <motion.div
              className="pricing-grid"
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              {[
                {
                  id: 'free-audit',
                  name: 'Free Audit',
                  description: 'Comprehensive review of your current cloud infrastructure.',
                  price: 0,
                  duration_days: 'Lifetime',
                  request_limit: 1,
                  features: [
                    'Full Architecture Review',
                    'Security Vulnerability Check',
                    'Cloud Cost Optimization Analysis',
                    'Performance Bottlenecks Report',
                    'Zero Commitment Required'
                  ]
                },
                ...plans
              ].map((plan, i) => (
                <motion.div
                  key={plan.id}
                  className={`pricing-card ${plan.name === 'Professional' ? 'featured' : ''}`}
                  variants={fadeUp}
                  whileHover={{ y: -8 }}
                >
                  {plan.name === 'Professional' && <div className="featured-badge">⭐ Most Popular</div>}
                  {plan.id === 'free-audit' && <div className="featured-badge" style={{background: '#10b981'}}>🎁 100% Free</div>}
                  <div className="plan-name">{plan.name}</div>
                  <div className="plan-desc">{plan.description}</div>
                  <div className="plan-price">
                    <span className="price-currency">$</span>
                    <span className="price-amount text-gradient">{Number(plan.price).toLocaleString()}</span>
                    {plan.id === 'free-audit' ? (
                      <div className="price-period">One-time report</div>
                    ) : (
                      <div className="price-period">/month · {plan.duration_days} days access</div>
                    )}
                  </div>
                  <ul className="plan-features">
                    {plan.features.map((f, fi) => (
                      <li key={fi}><span className="feature-check">✓</span>{f}</li>
                    ))}
                  </ul>
                  {plan.id === 'free-audit' ? (
                    <Link to="/contact?subject=Free%20Architecture%20Audit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', background: '#10b981', borderColor: '#10b981' }}>
                      Claim Free Audit →
                    </Link>
                  ) : (
                    <Link to="/pricing" className={`btn ${plan.name === 'Professional' ? 'btn-primary' : 'btn-secondary'}`} style={{ width: '100%', justifyContent: 'center' }}>
                      Get {plan.name} Plan →
                    </Link>
                  )}
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="pricing-grid">
              {['Starter', 'Professional', 'Enterprise'].map((name, i) => (
                <motion.div key={i} className={`pricing-card ${i === 1 ? 'featured' : ''}`} variants={fadeUp} whileHover={{ y: -8 }}>
                  <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                    Loading plans...
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ marginTop: 40 }}
          >
            <Link to="/pricing" className="btn btn-secondary btn-lg">View All Plans & Features →</Link>
          </motion.div>
        </div>
      </section>

      {/* ===== DEVELOPERS ===== */}
      <section className="section" id="developers" style={{ background: 'rgba(15,23,42,0.3)' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="badge">👨‍💻 Expert Team</span>
            <h2 className="section-title">Meet Our <span className="text-gradient">DevOps Experts</span></h2>
            <p className="section-subtitle">Real engineers with production experience solving your toughest problems.</p>
          </motion.div>

          <div style={{ display: 'flex', overflowX: 'auto', gap: 24, paddingBottom: 24, marginTop: 40, scrollbarWidth: 'thin' }} className="hide-scroll">
            {developers.map((dev, i) => (
              <motion.div
                key={dev.id}
                className="glass-card"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                style={{ minWidth: 320, maxWidth: 320, flexShrink: 0, display: 'flex', flexDirection: 'column' }}
                whileHover={{ y: -5 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                  {dev.image_url ? (
                    <img src={dev.image_url} alt={dev.name} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: 24 }}>
                      {dev.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18 }}>{dev.name}</h3>
                    <div style={{ color: 'var(--brand-primary)', fontWeight: 600, fontSize: 13 }}>{dev.role}</div>
                  </div>
                </div>
                <div style={{ marginBottom: 12, fontSize: 14, color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>Experience:</strong> {dev.experience}
                </div>
                <div style={{ marginBottom: 16, fontSize: 14, color: 'var(--text-secondary)', flex: 1, lineHeight: 1.6 }}>
                  <strong style={{ color: 'var(--text-primary)' }}>Expertise:</strong> {dev.expertise}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {dev.skills && dev.skills.split(',').filter(Boolean).map((s, idx) => (
                     <span key={idx} style={{ background: 'rgba(255,255,255,0.05)', padding: '4px 10px', borderRadius: 20, fontSize: 12, border: '1px solid rgba(255,255,255,0.1)' }}>{s.trim()}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="section" style={{ background: 'rgba(15,23,42,0.5)' }}>
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge">💬 Testimonials</span>
            <h2 className="section-title">What Our <span className="text-gradient">Engineers Say</span></h2>
            <p className="section-subtitle">Join thousands of DevOps professionals who've accelerated their careers with us.</p>
          </motion.div>

          <motion.div
            className="testimonials-grid"
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} className="testimonial-card" variants={fadeUp} whileHover={{ y: -6 }}>
                <div className="stars">{'★'.repeat(t.rating)}</div>
                <p className="testimonial-text">"{t.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{t.initials}</div>
                  <div>
                    <div className="author-name">{t.name}</div>
                    <div className="author-role">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="section">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{
              background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))',
              border: '1px solid rgba(99,102,241,0.3)',
              borderRadius: 24,
              padding: '70px 40px',
            }}
          >
            <span className="badge">🎯 Limited Spots</span>
            <h2 className="section-title" style={{ marginBottom: 16 }}>
              Ready to <span className="text-gradient">Level Up</span> Your DevOps Career?
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 18, marginBottom: 36, maxWidth: 500, margin: '0 auto 36px' }}>
              Join 2,500+ engineers who've transformed their careers with expert-guided DevOps mentorship.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/pricing" className="btn btn-primary btn-lg">🚀 Start Your Journey</Link>
              <a href="mailto:support@infravelox.com" className="btn btn-ghost btn-lg">💬 Talk to an Expert</a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
}
